import { useState, useMemo, useEffect, useRef } from 'react';
import { useApp } from '../lib/AppContext';
import StoryCardItem from '../components/StoryCardItem';
import Button from '../components/Button';
import Tag from '../components/Tag';
import { Music, ArrowRight, Search, X } from 'lucide-react';

const TOPIC_FILTERS = [
  'All',
  'Conflict',
  'Education',
  'Environment',
  'Health',
  'Housing',
  'Identity',
  'Justice',
  'Media',
  'Migration',
  'Work',
];

export default function Explore() {
  const {
    navigateTo,
    getCards,
    selectedTopicFilter,
    setTopicFilter,
  } = useApp();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const allCards = getCards();

  // Filter cards by search query and topic
  const cards = useMemo(() => {
    let filtered = allCards;

    // Apply search filter
    if (debouncedQuery.trim()) {
      const query = debouncedQuery.toLowerCase();
      filtered = filtered.filter(card =>
        card.songTitle.toLowerCase().includes(query) ||
        card.artistName.toLowerCase().includes(query)
      );
    }

    return [...filtered].sort((a, b) => a.songTitle.localeCompare(b.songTitle));
  }, [allCards, debouncedQuery]);

  const handleCardClick = (cardId: string) => {
    navigateTo('card-detail', cardId);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedQuery('');
    searchInputRef.current?.focus();
  };

  return (
    <div
      className="min-h-screen bg-[#111111]"
      style={{
        paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)'
      }}
    >
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-[#111111] border-b border-[#282828]">
        <div className="px-6 py-4">
          <h1 className="text-[20px] font-semibold text-[#F5F5F5]" style={{ letterSpacing: '-0.005em' }}>
            Explore
          </h1>
        </div>
      </div>

      {/* Search Input - Primary Control */}
      <div className="px-6 pt-5 pb-4 max-w-5xl mx-auto w-full">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#707070]" strokeWidth={2} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search songs or artists…"
            className="w-full bg-[#181818] border border-[#282828] rounded-lg pl-10 pr-10 py-2.5 text-[14px] text-[#F5F5F5] placeholder-[#707070] focus:outline-none focus:border-[#383838] transition-colors"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#707070] hover:text-[#A0A0A0] transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {/* Filter Chips - Secondary Controls */}
      <div className="max-w-5xl mx-auto w-full px-6 pb-2">
        {/* Mobile: Scrollable horizontal */}
        <div className="md:hidden">
          <div className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-emerald-900/50 scrollbar-track-transparent -mx-1">
            <div className="flex gap-2 px-1" style={{ width: 'max-content' }}>
              {TOPIC_FILTERS.map(topic => (
                <Tag
                  key={topic}
                  variant="chip"
                  selected={selectedTopicFilter === topic}
                  onClick={() => setTopicFilter(topic)}
                  compact
                >
                  {topic}
                </Tag>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Scrollable horizontal */}
        <div className="hidden md:block">
          <div className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-emerald-900/50 scrollbar-track-transparent -mx-1">
            <div className="flex gap-2 px-1" style={{ width: 'max-content' }}>
              {TOPIC_FILTERS.map(topic => (
                <Tag
                  key={topic}
                  variant="chip"
                  selected={selectedTopicFilter === topic}
                  onClick={() => setTopicFilter(topic)}
                >
                  {topic}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Result Count */}
      <div className="px-6 pt-2 pb-4 max-w-5xl mx-auto w-full">
        <p className="text-[12px] text-[#606060]">
          {debouncedQuery.trim()
            ? `Showing ${cards.length} song${cards.length !== 1 ? 's' : ''} for '${debouncedQuery}'`
            : `Showing ${cards.length} song${cards.length !== 1 ? 's' : ''}`
          }
        </p>
      </div>

      {/* Song Cards */}
      <div className="px-6 pb-4 max-w-5xl mx-auto w-full space-y-4">
        {cards.map((card, index) => (
          <div
            key={`${card.id}-${selectedTopicFilter}`}
            className="animate-fade-in-up"
            style={{
              animationDelay: `${index * 50}ms`,
              animationFillMode: 'both',
            }}
          >
            <StoryCardItem
              card={card}
              onClick={() => handleCardClick(card.id)}
            />
          </div>
        ))}
      </div>

      {/* Empty State */}
      {cards.length === 0 && (
        <div className="px-8 py-16 text-center max-w-5xl mx-auto">
          <p className="text-[15px] font-medium text-[#E0E0E0] mb-2">
            No matches found
          </p>
          <p className="text-[14px] text-[#808080] mb-6">
            Try a different tag or search.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            {debouncedQuery.trim() && (
              <button
                onClick={handleClearSearch}
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-[#A0A0A0] hover:text-[#F5F5F5] bg-[#1A1A1A] hover:bg-[#282828] transition-colors"
              >
                Clear search
              </button>
            )}
            {selectedTopicFilter !== 'All' && (
              <button
                onClick={() => setTopicFilter('All')}
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-[#A0A0A0] hover:text-[#F5F5F5] bg-[#1A1A1A] hover:bg-[#282828] transition-colors"
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
      )}

      {/* Contribute CTA */}
      <div className="px-6 mt-8 md:mt-10 lg:mt-14 mb-6 md:mb-8 max-w-5xl mx-auto w-full">
        <div className="bg-[#181818] rounded-[20px] px-8 pt-8 pb-6 text-center border border-[#282828]" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#40E0D0]/20 to-[#40E0D0]/10 rounded-full flex items-center justify-center">
            <Music className="w-8 h-8 text-[#40E0D0]" />
          </div>
          <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-2" style={{ letterSpacing: '-0.005em' }}>
            Help Us Grow the civicbeat Collection
          </h3>
          <p className="text-[14px] text-[#A0A0A0] mb-6" style={{ lineHeight: '1.55' }}>
            Know a song with a story? Share it with us.
          </p>
          <Button
            onClick={() => navigateTo('submit-song')}
            variant="secondary"
          >
            Submit a Song
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
